import { Button } from "@patient-watch/ui/button";
import { CHOICE_CONTROL_SIZE } from "@patient-watch/ui/questionnaire";
import { Input } from "@patient-watch/ui/input";
import { cn } from "@patient-watch/ui/utils";
import { Pencil, Plus, Trash2 } from "lucide-react";
import { type KeyboardEvent, type ReactNode, useState } from "react";

import type { ChoiceGroupOption } from "./ChoiceGroup";

/** The same box as a QuestionnaireChoice, so "Other" and the rename box sit in the list like answers. */
const CHOICE_ROW =
    "flex items-center gap-3 bg-white/70 dark:bg-slate-900/40 px-3 py-3.5 border-2 border-slate-300 rounded-lg min-h-14 md:text-md text-sm text-start";

/** A choice row being typed in: drawn as if picked. */
const CHOICE_ROW_ACTIVE = cn(CHOICE_ROW, "items-start bg-indigo-50 dark:bg-indigo-950 border-indigo-600");

/** The empty tick box or radio ring a choice row starts with. */
function ChoiceIndicator({ multiple, children }: { multiple: boolean; children?: ReactNode }) {
    return (
        <span
            aria-hidden="true"
            className={cn(
                CHOICE_CONTROL_SIZE,
                "flex justify-center items-center bg-white dark:bg-slate-900 border-2 border-slate-400 text-slate-500 shrink-0",
                multiple ? "rounded-md" : "rounded-full"
            )}
        >
            {children}
        </span>
    );
}

/** Most characters in an own option (the sponsor's spec caps "Please specify" at 100). */
export const OWN_OPTION_MAX_LENGTH = 100;
/** Most own options one respondent may add to one question (the database enforces the same). */
export const OWN_OPTIONS_PER_QUESTION = 20;

/** Thrown by onDelete when an earlier answer uses the option, so it has to stay. */
export class OwnOptionInUseError extends Error {
    constructor() {
        super("This own option is used in another answer");
        this.name = "OwnOptionInUseError";
    }
}

export interface OwnOptionCopy {
    placeholder: string;
    add: string;
    save: string;
    cancel: string;
    edit: (label: string) => string;
    delete: (label: string) => string;
    empty: string;
    tooLong: string;
    limit: string;
    failed: string;
    inUse: string;
}

/**
 * "Add your own option" on a single- or multiple-choice question. The question's own text ("Other",
 * "Add option") opens a box; what the patient types becomes a private choice of theirs, picked straight
 * away. Their own choices carry edit and delete buttons in their own rows. Saving is the caller's: signed in
 * it goes to the database, on an anonymous public link it waits for submit.
 */
export interface AddOwnOptionProps {
    inputId: string;
    /** The button text: the question's add_option_label, or the default "Other". */
    label: string;
    /** The patient's own options on this question, as value (the option id) and text. */
    ownOptions: readonly { value: string; label: string }[];
    /** Saves a new own option and resolves to its value. */
    onAdd: (label: string) => Promise<string>;
    /** Renames one of the patient's own options. */
    onRename: (value: string, label: string) => Promise<void>;
    /** Deletes one of the patient's own options. Rejects with OwnOptionInUseError when it has to stay. */
    onDelete: (value: string) => Promise<void>;
    /** Picks the new option once it is saved. */
    onAdded: (value: string) => void;
    /** Checkbox question: draw a tick box rather than a radio ring, to match the other answers. */
    multiple?: boolean;
    copy: OwnOptionCopy;
}

function problemWith(text: string, copy: OwnOptionCopy): string | null {
    const trimmed = text.trim();
    if (trimmed.length === 0) return copy.empty;
    if (trimmed.length > OWN_OPTION_MAX_LENGTH) return copy.tooLong;
    return null;
}

/** A text box with its confirm and cancel buttons, shared by adding and renaming. */
function OwnOptionEditor({
    testId,
    initial,
    confirmText,
    copy,
    onConfirm,
    onCancel,
}: {
    testId: string;
    initial: string;
    confirmText: string;
    copy: OwnOptionCopy;
    onConfirm: (label: string) => Promise<void>;
    onCancel: () => void;
}) {
    const [text, setText] = useState(initial);
    const [error, setError] = useState<string | null>(null);
    const [saving, setSaving] = useState(false);

    const confirm = async () => {
        const problem = problemWith(text, copy);
        if (problem) {
            setError(problem);
            return;
        }
        setSaving(true);
        try {
            await onConfirm(text.trim());
        } catch {
            setError(copy.failed);
        } finally {
            setSaving(false);
        }
    };

    // Enter would submit the whole form page; here it confirms the box instead.
    const onKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
        if (event.key === "Enter") {
            event.preventDefault();
            void confirm();
        } else if (event.key === "Escape") {
            event.preventDefault();
            onCancel();
        }
    };

    return (
        <div className="flex-1 space-y-2 min-w-0" data-testid={testId}>
            <Input
                autoFocus
                value={text}
                maxLength={OWN_OPTION_MAX_LENGTH}
                placeholder={copy.placeholder}
                aria-invalid={error ? true : undefined}
                data-testid={`${testId}-text`}
                onChange={(event) => {
                    setText(event.target.value);
                    setError(null);
                }}
                onKeyDown={onKeyDown}
            />
            {error ? (
                <p className="text-destructive text-sm" role="alert">
                    {error}
                </p>
            ) : null}
            <div className="flex gap-2">
                <Button type="button" size="sm" disabled={saving} data-testid={`${testId}-confirm`} onClick={confirm}>
                    {confirmText}
                </Button>
                <Button
                    type="button"
                    size="sm"
                    variant="ghost"
                    disabled={saving}
                    data-testid={`${testId}-cancel`}
                    onClick={onCancel}
                >
                    {copy.cancel}
                </Button>
            </div>
        </div>
    );
}

/** The "Other" row: opens a box whose text becomes a new own option, picked straight away. */
function AddOwnOptionRow({
    inputId,
    label,
    atLimit,
    multiple,
    copy,
    onAdd,
    onAdded,
}: {
    inputId: string;
    label: string;
    atLimit: boolean;
    multiple: boolean;
    copy: OwnOptionCopy;
    onAdd: (label: string) => Promise<string>;
    onAdded: (value: string) => void;
}) {
    const [adding, setAdding] = useState(false);

    if (adding) {
        return (
            <div className={CHOICE_ROW_ACTIVE}>
                <ChoiceIndicator multiple={multiple} />
                <OwnOptionEditor
                    testId={`form-add-option-${inputId}-editor`}
                    initial=""
                    confirmText={copy.add}
                    copy={copy}
                    onConfirm={async (text) => {
                        onAdded(await onAdd(text));
                        setAdding(false);
                    }}
                    onCancel={() => setAdding(false)}
                />
            </div>
        );
    }
    if (atLimit) return <p className="text-muted-foreground text-sm">{copy.limit}</p>;
    return (
        <button
            type="button"
            className={cn(
                CHOICE_ROW,
                "w-full hover:bg-indigo-50 dark:hover:bg-indigo-950 transition-colors cursor-pointer outline-none focus-visible:ring-2 focus-visible:ring-indigo-600"
            )}
            data-testid={`form-add-option-${inputId}`}
            onClick={() => setAdding(true)}
        >
            <ChoiceIndicator multiple={multiple}>
                <Plus size={16} strokeWidth={3} />
            </ChoiceIndicator>
            <span className="flex-1 min-w-0 leading-snug">{label}</span>
        </button>
    );
}

/** Edit and delete, drawn inside the row of one of the patient's own options. */
function OwnOptionActions({
    value,
    label,
    copy,
    busy,
    onEdit,
    onDelete,
}: {
    value: string;
    label: string;
    copy: OwnOptionCopy;
    busy: boolean;
    onEdit: () => void;
    onDelete: () => void;
}) {
    return (
        <span className="flex items-center gap-1">
            <Button
                type="button"
                size="icon"
                variant="ghost"
                className="size-9"
                aria-label={copy.edit(label)}
                disabled={busy}
                data-testid={`form-own-option-edit-${value}`}
                onClick={onEdit}
            >
                <Pencil size={16} />
            </Button>
            <Button
                type="button"
                size="icon"
                variant="ghost"
                className="size-9 hover:text-destructive"
                aria-label={copy.delete(label)}
                disabled={busy}
                data-testid={`form-own-option-delete-${value}`}
                onClick={onDelete}
            >
                <Trash2 size={16} />
            </Button>
        </span>
    );
}

/**
 * Wires "add your own option" into a ChoiceGroup: the patient's own options get edit and delete buttons in
 * their rows (a rename swaps the row for a text box in place), and "Other" comes as the group's last row.
 * Pass `decorate(options)` as the group's options and `footer` as its footer.
 */
export function useOwnOptionRows({
    addOwnOption,
    inputId,
    multiple = false,
    onAdded,
    onDeleted,
}: {
    addOwnOption: Omit<AddOwnOptionProps, "inputId" | "onAdded" | "multiple"> | undefined;
    inputId: string;
    multiple?: boolean;
    /** Picks the new option once it is saved. */
    onAdded: (value: string) => void;
    /** Takes a deleted option out of the answer. */
    onDeleted: (value: string) => void;
}): { decorate: (options: ChoiceGroupOption[]) => ChoiceGroupOption[]; footer: ReactNode } {
    const [editing, setEditing] = useState<string | null>(null);
    const [deleting, setDeleting] = useState<string | null>(null);
    const [deleteError, setDeleteError] = useState<string | null>(null);

    if (!addOwnOption) return { decorate: (options) => options, footer: null };
    const { copy, ownOptions, onAdd, onRename, onDelete } = addOwnOption;
    const ownLabels = new Map(ownOptions.map((option) => [option.value, option.label]));

    const remove = async (value: string) => {
        setDeleting(value);
        setDeleteError(null);
        try {
            await onDelete(value);
            onDeleted(value);
        } catch (error) {
            setDeleteError(error instanceof OwnOptionInUseError ? copy.inUse : copy.failed);
        } finally {
            setDeleting(null);
        }
    };

    const decorate = (options: ChoiceGroupOption[]) =>
        options.map((option) => {
            const label = ownLabels.get(option.value);
            if (label === undefined) return option;
            if (editing === option.value) {
                return {
                    ...option,
                    replacement: (
                        <div className={CHOICE_ROW_ACTIVE}>
                            <ChoiceIndicator multiple={multiple} />
                            <OwnOptionEditor
                                testId={`form-own-option-rename-${option.value}`}
                                initial={label}
                                confirmText={copy.save}
                                copy={copy}
                                onConfirm={async (text) => {
                                    if (text !== label) await onRename(option.value, text);
                                    setEditing(null);
                                }}
                                onCancel={() => setEditing(null)}
                            />
                        </div>
                    ),
                };
            }
            return {
                ...option,
                trailing: (
                    <OwnOptionActions
                        value={option.value}
                        label={label}
                        copy={copy}
                        busy={deleting === option.value}
                        onEdit={() => {
                            setDeleteError(null);
                            setEditing(option.value);
                        }}
                        onDelete={() => void remove(option.value)}
                    />
                ),
            };
        });

    const footer = (
        <div className="space-y-2" data-testid={`form-own-options-${inputId}`}>
            {deleteError ? (
                <p className="text-destructive text-sm" role="alert">
                    {deleteError}
                </p>
            ) : null}
            <AddOwnOptionRow
                inputId={inputId}
                label={addOwnOption.label}
                atLimit={ownOptions.length >= OWN_OPTIONS_PER_QUESTION}
                multiple={multiple}
                copy={copy}
                onAdd={onAdd}
                onAdded={onAdded}
            />
        </div>
    );

    return { decorate, footer };
}
